import React, { useState, useEffect, useRef } from 'react';
import { Player, PlayerStats } from '../../types';
import { PlayerCard } from './PlayerCard';
import { WebPlayerCard } from './WebPlayerCard';
import { useResponsive } from '../../hooks/useResponsive';

interface SubstitutesProps {
  substitutes: Player[];
  userIsCreator: boolean;
  onRemovePlayer: (playerId: string) => void;
  onSelfRemove?: (playerId: string) => void;
  onEditPlayer?: (player: Player) => void;
  onCreateReview?: (player: Player) => void;
  onViewReviews?: (player: Player) => void;
  onViewProfile?: (player: Player) => void;
  currentUserId?: string;
  canViewReviews?: boolean;
  onJoinAsSubstitute: () => void;
  playerStats?: { [playerId: string]: PlayerStats };
  matchStatus?: string;
  mvpPlayerIds?: string[];
  topScorerPlayerIds?: string[];
  topAssisterPlayerIds?: string[];
  topSaverPlayerIds?: string[];
  creatorId?: string;
  // Nuevas props para drag & drop
  onDragStart?: (player: Player) => void;
  onDragEnd?: () => void;
  onDragOver?: (player: Player | null) => void;
  onDrop?: (player: Player | null) => void;
  isDragging?: boolean;
  draggedPlayer?: Player | null;
  dragOverPlayer?: Player | null;
  // Props para reviews
  hasReviewedPlayer?: { [playerId: string]: boolean };
  loadingReviews?: boolean;
  // Props para validación de stats
  onValidateStats?: (player: Player) => void;
  hasPendingStats?: { [playerId: string]: boolean };
  // Props para organizer panel
  allPlayers?: Player[];
  onSetPlayerSkill?: (playerId: string, score: number) => void;
  onLinkFriends?: (playerAId: string, playerBId: string) => void;
  onUnlinkFriend?: (playerId: string) => void;
  // Compact mode (desktop strip)
  compact?: boolean;
  onPromoteSubstitute?: (playerId: string) => void;
  onGenerateReplacementLink?: (player: Player) => void;
}

// ── Compact menu styles (same as WebSoccerField) ──────────────────────────────
const menuBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#fff',
  fontSize: 12,
  fontWeight: 500,
  padding: '6px 10px',
  cursor: 'pointer',
  textAlign: 'left',
  borderRadius: 6,
  transition: 'background 0.15s',
};
const menuBtnHoverIn = (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; };
const menuBtnHoverOut = (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.background = 'none'; };

export const Substitutes: React.FC<SubstitutesProps> = ({
  substitutes,
  userIsCreator,
  onRemovePlayer,
  onSelfRemove,
  onEditPlayer,
  onCreateReview,
  onViewReviews,
  onViewProfile,
  currentUserId,
  canViewReviews,
  onJoinAsSubstitute,
  playerStats,
  matchStatus,
  mvpPlayerIds = [],
  topScorerPlayerIds = [],
  topAssisterPlayerIds = [],
  topSaverPlayerIds = [],
  creatorId,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  isDragging = false,
  draggedPlayer,
  dragOverPlayer,
  hasReviewedPlayer = {},
  loadingReviews = false,
  onValidateStats,
  hasPendingStats = {},
  allPlayers,
  onSetPlayerSkill,
  onLinkFriends,
  onUnlinkFriend,
  compact = false,
  onPromoteSubstitute,
  onGenerateReplacementLink,
}) => {
  const [activeSubMenu, setActiveSubMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on click outside
  useEffect(() => {
    if (!activeSubMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setActiveSubMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeSubMenu]);
  const handleDragStart = (e: React.DragEvent, player: Player) => {
    if (!userIsCreator) {
      e.preventDefault();
      return;
    }
    
    // Configurar drag preview personalizado
    e.dataTransfer.setData('text/plain', player.id);
    e.dataTransfer.effectAllowed = 'move';
    
    // Crear un elemento invisible para el drag preview nativo
    const dragImage = document.createElement('div');
    dragImage.style.position = 'absolute';
    dragImage.style.top = '-1000px';
    dragImage.style.left = '-1000px';
    dragImage.style.width = '1px';
    dragImage.style.height = '1px';
    dragImage.style.opacity = '0';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    
    // Remover el elemento después de un frame
    setTimeout(() => {
      if (document.body.contains(dragImage)) {
        document.body.removeChild(dragImage);
      }
    }, 0);
    
    onDragStart?.(player);
  };

  const handleDragOver = (e: React.DragEvent, player: Player) => {
    if (!userIsCreator || !draggedPlayer || draggedPlayer.id === player.id) {
      e.preventDefault();
      return;
    }
    
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    onDragOver?.(player);
  };

  const handleDrop = (e: React.DragEvent, player: Player) => {
    if (!userIsCreator || !draggedPlayer || draggedPlayer.id === player.id) {
      e.preventDefault();
      return;
    }
    
    e.preventDefault();
    onDrop?.(player);
  };

  const handleDragEnd = () => {
    onDragEnd?.();
  };

  // Función para manejar drop en el área de suplentes (no en un jugador específico)
  const handleSubstitutesAreaDrop = (e: React.DragEvent) => {
    if (!userIsCreator || !draggedPlayer) {
      e.preventDefault();
      return;
    }
    
    // Solo permitir mover jugadores principales a suplentes
    if (!draggedPlayer.isSubstitute) {
      e.preventDefault();
      onDrop?.(null); // null indica que se mueve al área de suplentes
    }
  };

  const handleSubstitutesAreaDragOver = (e: React.DragEvent) => {
    if (!userIsCreator || !draggedPlayer) {
      e.preventDefault();
      return;
    }
    
    // Solo permitir mover jugadores principales a suplentes
    if (!draggedPlayer.isSubstitute) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      onDragOver?.(null); // null indica que se mueve al área de suplentes
    }
  };

  const isDragOverArea = dragOverPlayer === null && isDragging && draggedPlayer && !draggedPlayer.isSubstitute;

  const { isMobile, isCompact } = useResponsive();

  // ── Compact horizontal strip (desktop) ────────────────────────────────────
  if (compact) {
    return (
      <div
        data-testid="substitutes-strip"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '8px 16px',
          background: isDragOverArea ? 'rgba(255, 215, 0, 0.15)' : 'rgba(0,0,0,0.3)',
          borderRadius: '0 0 12px 12px',
          border: isDragOverArea ? '2px dashed #FFD700' : '1px solid rgba(255,255,255,0.08)',
          transition: 'background 0.2s',
          overflowX: 'auto',
          flexShrink: 0,
        }}
        onDragOver={handleSubstitutesAreaDragOver}
        onDrop={handleSubstitutesAreaDrop}
      >
        {/* Label */}
        <span style={{
          fontSize: 10,
          fontWeight: 700,
          color: '#ccc',
          letterSpacing: 1,
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}>
          SUPLENTES ({substitutes.length})
        </span>

        {/* Sub cards */}
        {substitutes.map((player) => {
          const isBeingDragged = draggedPlayer?.id === player.id;
          return (
            <div
              key={`sub-compact-${player.id}`}
              ref={activeSubMenu === player.id ? menuRef : undefined}
              style={{ position: 'relative', flexShrink: 0, opacity: isBeingDragged ? 0.4 : 1, cursor: 'grab' }}
              draggable={userIsCreator}
              onDragStart={(e) => handleDragStart(e, player)}
              onDragEnd={handleDragEnd}
            >
              <WebPlayerCard
                name={player.name}
                position={player.position}
                rating={player.rating}
                teamColor="#888"
                size="md"
                nameMaxChars={10}
                avatar={player.user?.avatar}
                isGuest={!player.userId}
                isCurrentUser={currentUserId === player.userId}
                onClick={() => setActiveSubMenu(activeSubMenu === player.id ? null : player.id)}
              />
              {/* Context menu */}
              {activeSubMenu === player.id && (
                <div
                  data-testid="sub-action-menu"
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    marginBottom: 4,
                    background: 'rgba(30,30,30,0.95)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: 10,
                    padding: 6,
                    zIndex: 30,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    border: '1px solid rgba(255,255,255,0.15)',
                    minWidth: 140,
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  <div style={{ fontSize: 10, color: '#999', padding: '2px 8px', fontWeight: 700 }}>
                    {player.name}
                  </div>
                  {onViewProfile && player.userId && (
                    <button onClick={() => { onViewProfile(player); setActiveSubMenu(null); }}
                      style={menuBtnStyle} onMouseEnter={menuBtnHoverIn} onMouseLeave={menuBtnHoverOut}>
                      Ver perfil
                    </button>
                  )}
                  {onPromoteSubstitute && matchStatus !== 'FINISHED' && (
                    <button
                      data-testid="sub-action-promote"
                      onClick={() => { onPromoteSubstitute(player.id); setActiveSubMenu(null); }}
                      style={{...menuBtnStyle, color: '#4CAF50'}} onMouseEnter={menuBtnHoverIn} onMouseLeave={menuBtnHoverOut}>
                      Promover a titular
                    </button>
                  )}
                  {userIsCreator && matchStatus !== 'FINISHED' && (
                    <button
                      data-testid="sub-action-remove"
                      onClick={() => { onRemovePlayer(player.id); setActiveSubMenu(null); }}
                      style={{...menuBtnStyle, color: '#F44336'}} onMouseEnter={menuBtnHoverIn} onMouseLeave={menuBtnHoverOut}>
                      Sacar del partido
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Compact add button */}
        {userIsCreator && matchStatus !== 'FINISHED' && (
          <button
            onClick={onJoinAsSubstitute}
            data-testid="sub-add-btn"
            style={{
              background: 'rgba(255, 152, 0, 0.2)',
              border: '2px dashed #F57C00',
              borderRadius: 8,
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: 18,
              color: '#FF9800',
              flexShrink: 0,
              transition: 'all 0.15s',
              padding: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 152, 0, 0.4)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 152, 0, 0.2)'; }}
            title="Agregar suplente"
          >
            +
          </button>
        )}
      </div>
    );
  }

  // ── Full vertical layout (mobile/compact) ─────────────────────────────────
  return (
    <div 
      style={{ 
        backgroundColor: isDragOverArea ? 'rgba(255, 215, 0, 0.2)' : '#23272A',
        padding: (isMobile || isCompact) ? '12px' : '20px',
        borderRadius: '18px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        marginTop: (isMobile || isCompact) ? '12px' : '20px',
        marginBottom: (isMobile || isCompact) ? '12px' : '20px',
        border: isDragOverArea ? '2px dashed #FFD700' : '1px solid #33383B',
        minHeight: '100px',
        position: 'relative',
        transform: isDragOverArea ? 'scale(1.02)' : 'scale(1)',
        transition: 'all 0.2s ease'
      }}
      onDragOver={handleSubstitutesAreaDragOver}
      onDrop={handleSubstitutesAreaDrop}
    >
      {isDragOverArea && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 215, 0, 0.3)',
          border: '2px dashed #FFD700',
          borderRadius: '18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          fontSize: '16px',
          fontWeight: 'bold',
          color: '#FFD700',
          textShadow: '0 0 4px rgba(0,0,0,0.8)'
        }}>
          ⬇️ MOVER A SUPLENTES
        </div>
      )}
      <h2 style={{ 
        color: '#00E676',
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
        letterSpacing: '0.08em',
        marginBottom: (isMobile || isCompact) ? '12px' : '20px',
        fontSize: (isMobile || isCompact) ? '16px' : '18px'
      }}>
        🔄 Suplentes ({substitutes.length})
      </h2>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: (isMobile || isCompact) ? '8px' : '12px' }}>
        {/* Lista de suplentes existentes */}
        {substitutes.length > 0 && (
          <div>
            {substitutes.map((player: Player, index: number) => {
              const isDragOver = dragOverPlayer?.id === player.id;
              const isBeingDragged = draggedPlayer?.id === player.id;
              
              return (
                <div
                  key={`substitute-${player.id}`}
                  draggable={userIsCreator}
                  onDragStart={(e) => handleDragStart(e, player)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, player)}
                  onDrop={(e) => handleDrop(e, player)}
                  style={{
                    opacity: isBeingDragged ? 0.5 : 1,
                    transform: isDragOver ? 'scale(1.05)' : 'scale(1)',
                    transition: 'all 0.2s ease',
                    position: 'relative'
                  }}
                >
                  {isDragOver && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 215, 0, 0.3)',
                      border: '2px dashed #FFD700',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: '#FFD700',
                      textShadow: '0 0 4px rgba(0,0,0,0.8)'
                    }}>
                      🔄 INTERCAMBIAR
                    </div>
                  )}
                  <PlayerCard
                    player={player}
                    userIsCreator={userIsCreator}
                    onRemovePlayer={onRemovePlayer}
                    onSelfRemove={onSelfRemove}
                    onEditPlayer={onEditPlayer}
                    onCreateReview={onCreateReview}
                    onViewReviews={onViewReviews}
                    onViewProfile={onViewProfile}
                    onValidateStats={onValidateStats}
                    currentUserId={currentUserId}
                    canViewReviews={canViewReviews}
                    playerStats={playerStats?.[player.id]}
                    matchStatus={matchStatus}
                    isMVP={mvpPlayerIds.includes(player.id)}
                    isTopScorer={topScorerPlayerIds.includes(player.id)}
                    isTopAssister={topAssisterPlayerIds.includes(player.id)}
                    isTopSaver={topSaverPlayerIds.includes(player.id)}
                    isMatchCreator={creatorId === player.userId}
                    isDragging={isDragging}
                    isDragOver={isDragOver}
                    hasReviewedPlayer={hasReviewedPlayer[player.id] || false}
                    loadingReviews={loadingReviews}
                    hasPendingStats={hasPendingStats[player.id] || false}
                    allPlayers={allPlayers}
                    onSetPlayerSkill={onSetPlayerSkill}
                    onLinkFriends={onLinkFriends}
                    onUnlinkFriend={onUnlinkFriend}
                    onGenerateReplacementLink={onGenerateReplacementLink}
                  />
                </div>
              );
            })}
          </div>
        )}
        
        {/* Botón para agregar suplente */}
        {userIsCreator && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: (isMobile || isCompact) ? '12px' : '16px'
          }}>
            <button
              onClick={onJoinAsSubstitute}
              style={{
                padding: (isMobile || isCompact) ? '10px 20px' : '12px 24px',
                backgroundColor: '#FF9800',
                color: '#FFFFFF',
                border: '2px dashed #F57C00',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: (isMobile || isCompact) ? '12px' : '14px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: 'fit-content',
                minWidth: '180px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F57C00';
                e.currentTarget.style.borderColor = '#FF9800';
                e.currentTarget.style.transform = 'scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#FF9800';
                e.currentTarget.style.borderColor = '#F57C00';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <span>👥</span>
              <span>AGREGAR SUPLENTE</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}; 